import { Divider, Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { FaDownload } from 'react-icons/fa';
import { Button, CardHeader, ChevronDown, Column, Content, FilterAlt, MainCard, NoContent, Pagination, Row, Title } from 'src/common';
import { PieChart, PieChartDataProps } from 'src/common/components/graphics/pie-chart';
import RadioButton from 'src/common/components/radio-button';

import { BarChartDataStackProps, BarChartStack } from '../../../common/components/graphics/bar-chart-stack';
import UpdateData from '../../../common/components/update-data';
import DialogModal from '../../../modules/analytics/face-missing/components/dialog-modal';
import { FaceMissingFilter } from '../../../modules/analytics/face-missing/components/face-missing-filter';
import TableList from '../../../modules/analytics/face-missing/components/table-list';
import { useFaceMissing } from '../../../modules/analytics/face-missing/hook';
import { GraphSection, GraphContainer, GraphTitle, FilterContainer, DetailsContainer, DetailsSection, PageChange } from './styles';

export function FaceMissing() {
    const { t } = useTranslation();

    const {
        handleGetList,
        responseStatusDetails,
        handleRequestList,
        handleGetListDetails,
        listFaceMissing,
        requestData,
        listFaceMissingDetails,
        handleFilterIsOpen,
        filterIsOpen,
        responseStatus,
    } = useFaceMissing();

    const filters = useMemo(() => {
        if (filterIsOpen) {
            return <FaceMissingFilter />;
        }

        return null;
    }, [filterIsOpen]);

    const [dialogIsOpen, setDialogIsOpen] = useState(false);

    useEffect(() => {
        return () => {
            handleRequestList({
                currentPage: 1,
                pageSize: 10,
            });
        };
    }, [handleRequestList]);

    const handleOpenDialog = useCallback(() => {
        setDialogIsOpen(state => !state);
    }, []);

    const [downloadFormat, setDownloadFormat] = useState<string>('CSV');

    const handleUpdate = useCallback(async () => {
        handleGetList();
        handleGetListDetails();
    }, [handleGetList, handleGetListDetails]);

    useEffect(() => {
        handleUpdate();
    }, [handleUpdate]);

    const paginationContent = useMemo(() => {
        return (
            <>
                <div>
                    <Divider />
                    <Pagination
                        onPageChange={currentPage => handleRequestList({ currentPage })}
                        currentPage={requestData?.currentPage}
                        perPage={requestData?.pageSize}
                        total={listFaceMissingDetails?.totalElements}
                    />
                </div>
            </>
        );
    }, [handleRequestList, listFaceMissingDetails?.totalElements, requestData.currentPage, requestData.pageSize]);

    const content = useMemo(() => {
        if (responseStatus.loading) {
            return (
                <Content>
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                    <Skeleton />
                </Content>
            );
        } else if (responseStatusDetails?.hasError) {
            return <NoContent message={t('general.error')} onClick={handleGetListDetails} messageClassName="error" />;
        } else if (responseStatusDetails?.void) {
            return <NoContent message={t('general.no_content')} onClick={handleGetListDetails} />;
        }

        return <TableList />;
    }, [handleGetListDetails, responseStatus.loading, responseStatusDetails?.hasError, responseStatusDetails?.void, t]);

    const renderDataPieChart = useMemo(() => {
        const content: Array<PieChartDataProps> = [];

        const faceMissingGroup: Array<{ name: string; total: number }> = [];

        listFaceMissing?.forEach(faceMissing => {
            const index = faceMissingGroup.findIndex(data => data.name === faceMissing.status);

            if (index !== -1) {
                faceMissingGroup[index].total += 1;
            } else {
                faceMissingGroup.push({
                    name: faceMissing.status,
                    total: 1,
                });
            }
        });

        faceMissingGroup?.forEach(data => {
            content.push({
                name: data.name,
                value: data.total,
                color: data.name === 'Deslocado' ? '#4f8dca' : '#87b6d1',
            });
        });

        return content;
    }, [listFaceMissing]);

    const renderStatusPerOperationLegends = useMemo(() => {
        const legends = [...new Set(listFaceMissing?.map(data => data.fleet))];

        return legends;
    }, [listFaceMissing]);

    const renderStatusPerOperationData = useMemo(() => {
        const content: Array<BarChartDataStackProps> = [];

        content.push({
            label: 'Deslocado',
            data: [],
            color: '#4f8dca',
        });

        content.push({
            label: 'Ok',
            data: [],
            color: '#87b6d1',
        });

        listFaceMissing?.map(data => {
            content[data.status === 'Deslocado' ? 1 : 0].data.push(0);
            content[data.status === 'Deslocado' ? 0 : 1].data.push(Number(data.percentage.toFixed(2)));
        });

        return content;
    }, [listFaceMissing]);

    const contentPieChart = useMemo(() => {
        if (responseStatus?.loading) {
            return (
                <Content style={{ display: 'flex', flexDirection: 'column' }}>
                    <Skeleton height={'4rem'} width={'20rem'} />
                    <Skeleton height={'3rem'} width={'40rem'} style={{ marginTop: '-1.5rem' }} />
                    <Skeleton height={'32rem'} style={{ marginTop: '-7rem' }} />
                </Content>
            );
        } else if (responseStatus?.hasError) {
            return <NoContent message={t('general.error')} onClick={handleGetList} messageClassName="error" />;
        } else if (responseStatus?.void) {
            return <NoContent message={t('general.no_content')} onClick={handleGetList} />;
        }

        return (
            <>
                <GraphTitle>
                    <div>
                        <Title>
                            <h2>{t('face_missing.proportion')}</h2>
                        </Title>
                        <p>{t('face_missing.proportion_graph_subtitle')}</p>
                    </div>
                </GraphTitle>
                <PieChart data={renderDataPieChart} />
            </>
        );
    }, [handleGetList, renderDataPieChart, responseStatus?.hasError, responseStatus?.loading, responseStatus?.void, t]);

    const contentPolarChart = useMemo(() => {
        if (responseStatus?.loading) {
            return (
                <Content style={{ display: 'flex', flexDirection: 'column' }}>
                    <Skeleton height={'4rem'} width={'20rem'} />
                    <Skeleton height={'3rem'} width={'40rem'} style={{ marginTop: '-1.5rem' }} />
                    <Skeleton height={'32rem'} style={{ marginTop: '-7rem' }} />
                </Content>
            );
        } else if (responseStatus?.hasError) {
            return <NoContent message={t('general.error')} onClick={handleGetList} messageClassName="error" />;
        } else if (responseStatus?.void) {
            return <NoContent message={t('general.no_content')} onClick={handleGetList} />;
        }

        return (
            <>
                <GraphTitle>
                    <div>
                        <Title>
                            <h2>{t('face_missing.fleet')}</h2>
                        </Title>
                        <p>{t('face_missing.fleet_graph_subtitle')}</p>
                    </div>

                    <div className="button-container">
                        <button onClick={handleOpenDialog}>
                            <AiOutlineInfoCircle color="#46526C" size={20} />
                        </button>
                    </div>
                </GraphTitle>
                <BarChartStack
                    legends={renderStatusPerOperationLegends}
                    data={renderStatusPerOperationData}
                    isDataZoom
                    dataZoom={{
                        start: 0,
                        end: 1,
                    }}
                />
            </>
        );
    }, [
        handleGetList,
        handleOpenDialog,
        renderStatusPerOperationData,
        renderStatusPerOperationLegends,
        responseStatus?.hasError,
        responseStatus?.loading,
        responseStatus?.void,
        t,
    ]);

    const contentTable = useMemo(() => {
        if (responseStatusDetails?.loading) {
            return (
                <Content style={{ display: 'flex', flexDirection: 'column' }}>
                    <Skeleton height={'4rem'} width={'20rem'} />
                    <Skeleton height={'3rem'} width={'40rem'} style={{ marginTop: '-1.5rem' }} />
                    <Skeleton height={'32rem'} style={{ marginTop: '-7rem' }} />
                </Content>
            );
        } else if (responseStatusDetails?.hasError) {
            return <NoContent message={t('general.error')} onClick={handleGetListDetails} messageClassName="error" />;
        } else if (responseStatusDetails?.void) {
            return <NoContent message={t('general.no_content')} onClick={handleGetListDetails} />;
        }

        return (
            <>
                <GraphTitle>
                    <div>
                        <Title>
                            <h2>{t('face_missing.details')}</h2>
                        </Title>
                        <p>{t('face_missing.details_table_subtitle')}</p>
                    </div>
                </GraphTitle>
                {/* <FilterContainer>
                    <div onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDownloadFormat(event.target.value)}>
                        <Button color="outline" size="medium">
                            <FaDownload />
                        </Button>

                        <RadioButton group="DownloadFormat" checked={downloadFormat === 'CSV'} name="CSV" value="CSV" />
                        <RadioButton group="DownloadFormat" checked={downloadFormat === 'EXCEL'} name="EXCEL" value="EXCEL" />
                        <RadioButton group="DownloadFormat" checked={downloadFormat === 'PDF'} name="PDF" value="PDF" />
                    </div>
                </FilterContainer> */}
                {content}
                <PageChange>{paginationContent}</PageChange>
            </>
        );
    }, [
        content,
        handleGetListDetails,
        paginationContent,
        responseStatusDetails?.hasError,
        responseStatusDetails?.loading,
        responseStatusDetails?.void,
        t,
    ]);

    return (
        <Column gap={16}>
            <Row align="center" width="100%" justify="space-between">
                <Title>
                    <h2>Analytics</h2>
                    <UpdateData onClick={handleUpdate} />
                </Title>
            </Row>

            <MainCard style={{ paddingBottom: '1rem', overflowY: 'hidden' }}>
                <Column width="100%" gap={16}>
                    <CardHeader>
                        <Row gap={16} align="center">
                            <h3>Face Missing</h3>
                        </Row>

                        <Row align="flex-start" gap={16}>
                            <Row align="center" gap={8}>
                                <Button
                                    color={filterIsOpen ? 'purple' : 'outline-purple'}
                                    leftIcon={<FilterAlt color={filterIsOpen ? '#ffffff' : '#46526C'} />}
                                    size="small"
                                    rightIcon={<ChevronDown spin={filterIsOpen} color={filterIsOpen ? '#fff' : '#46526C'} />}
                                    type="button"
                                    onClick={handleFilterIsOpen}
                                >
                                    {t('general.filter')}
                                </Button>
                            </Row>
                        </Row>
                    </CardHeader>
                    <Divider />
                    <div>{filters}</div>
                </Column>

                <Row padding="0.8rem" style={{ gap: '1.5rem' }}>
                    <GraphSection>
                        <GraphContainer>{contentPieChart}</GraphContainer>
                        <GraphContainer>{contentPolarChart}</GraphContainer>
                    </GraphSection>
                    <DetailsSection>
                        <DetailsContainer> {contentTable}</DetailsContainer>
                    </DetailsSection>
                </Row>

                <DialogModal onClose={handleOpenDialog} open={dialogIsOpen}>
                    <div>
                        <h2>{t('face_missing.informative')}</h2>
                    </div>
                    <div>
                        <h3>{t('face_missing.about_face_missing')}</h3>
                        <p>{t('face_missing.a_fleet_is_classified')}</p>
                    </div>
                </DialogModal>
            </MainCard>
        </Column>
    );
}
